import React, { useEffect, useRef } from 'react';
import { Grid, Typography, Box, Button } from '@material-ui/core';
import { useMutation } from '@apollo/react-hooks';
import { useSnackbar } from 'notistack';
import { Form } from '../../components/Form';
import { POST_SIMULATION_DATA } from '../graphQL/queries';
import JsonEditor from '../../components/JsonEditor';
import moment from 'moment';
import _ from 'lodash';
import useStyles from './Style';
import {
  getSimulationFields,
  getInputField,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  formatString
} from '../../utils/helpers';
import ArrowChevronDown from '@jda/lui-common-icon-library/ui/ArrowChevronDown';
interface CertificationFormProps {
  participant: any;
  workflowCode: string;
  testNames?: any;
  initiator?: String;
  customerCode?: String;
  providerCode?: String;
  simulationType?: String;
  simulationName?: String;
  eventType?: String;
  partner?: String;
  resetForm: () => void;
}

const CertificationForm: any = (props: CertificationFormProps) => {
  const classes = useStyles();
  const [fields, setFields] = React.useState<any>([]);
  const [input, setInput] = React.useState<any>('');
  const [response, setResponse] = React.useState<any>('');
  const [request, setRequest] = React.useState<any>('');

  const { workflowCode, testNames } = props;

  const [postSimulatedData] = useMutation(POST_SIMULATION_DATA);
  const { enqueueSnackbar } = useSnackbar();

  const setDate = (value: any, key: any) => {
    if (fields.length > 0) {
      fields.map((item: any) => {
        if (item.key === key) item.defaultValue = value;
      });
      setFields(fields);
    }
  };

  useEffect(() => {
    setSimulationForm();
  }, [props?.simulationName, props?.eventType, testNames]);

  const setSimulationForm = () => {
    if (testNames?.length > 0 && props?.eventType && props?.simulationName) {
      const simulation = testNames?.filter(
        (simulation: any) =>
          simulation?.operation === props?.simulationName && simulation?.testDataName === props?.eventType
      );
      if (simulation?.length > 0) {
        const fields = getSimulationFields(simulation[0]?.configSpec);
        const input = formatString(simulation[0]?.input);
        setFields(fields);
        setInput(input);
      }
    }
  };

  const handleSimulationSubmit = (formData: any) => {
    setRequest('');
    setResponse('');
    formData['partner'] = props?.partner;
    const inputFields = getInputField(fields, formData, input);
    const payload = {
      apiHeader: {
        companyCode: 'BlueYonder',
        contractedCompanyCode: 'BYN',
        customerCode: [props?.customerCode],
        messageID: Math.random()
          .toString(36)
          .substr(2, 5),
        providerCode: [props?.providerCode],
        timestamp: moment(new Date()).format('YYYY-MM-DDTHH:mm:ss[Z]')
      },
      ...inputFields
    };

    let search = '';

    if (workflowCode === 'freightTracking') {
      search = `?partner=${props?.partner}&customerCode=${props?.participant?.participantCode}`;
    }
    postSimulatedData({
      variables: { workflowCode: workflowCode, operationCode: props?.simulationType, payload: { ...payload }, search }
    })
      .then((response: any) => {
        enqueueSnackbar('Simulation Executed  Successfully !!', {
          variant: 'success',
          preventDuplicate: true
        });
        setResponse(response?.data?.postsimulatedData?.response);
        setRequest(response?.data?.postsimulatedData?.request);
        setResponseEditorRef(response);
      })
      .catch((error: any) => {
        setResponse(error.networkError.result.errors);
        enqueueSnackbar(error?.networkError?.result?.errors?.[0]?.userMessage || 'Simulation Update failed !!', {
          variant: 'error',
          preventDuplicate: true
        });
      });
  };

  const jsonRequestEditorRef = useRef<any>();
  const jsonResponseEditorRef = useRef<any>();

  const setRequestEditorRef = (instance: any) => {
    if (instance) {
      jsonRequestEditorRef.current = instance.jsonEditor;
    } else {
      jsonRequestEditorRef.current = null;
    }
  };

  const setResponseEditorRef = (instance: any) => {
    if (instance) {
      jsonResponseEditorRef.current = instance.jsonEditor;
    } else {
      jsonResponseEditorRef.current = null;
    }
  };

  const refreshForm = () => {
    setSimulationForm();
    setRequest('');
    setResponse('');
  };

  return (
    <Box style={{ width: '100%', padding: '0 10px' }}>
      <Grid container alignItems="center" className={classes.content}>
        <Grid item xs={1} />
        <Grid item xs={10}>
          <Form
            className={classes.root}
            dataConfig={fields}
            handleDialogSubmit={handleSimulationSubmit}
            submitlabel="Submit"
            setDate={setDate}
          >
            {fields?.length === 0 && (
              <Button
                name="subit-request"
                onClick={handleSimulationSubmit}
                type="submit"
                variant="contained"
                color="primary"
                style={{ float: 'right', marginLeft: 25 }}
              >
                Submit
              </Button>
            )}
            <Button
              name="refresh-request"
              onClick={refreshForm}
              type="submit"
              variant="contained"
              color="primary"
              style={{ float: 'right' }}
            >
              Refresh
            </Button>
          </Form>
        </Grid>
        <Grid item xs={1}></Grid>
      </Grid>
      {response && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Accordion elevation={0} onChange={(e, expanded) => setInput(input)} expanded={input !== ''}>
              <AccordionSummary
                expandIcon={<ArrowChevronDown color="inherit" />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>Simulation Request</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {request && (
                  <Grid item xs={12}>
                    <JsonEditor
                      data-id="transform-spec-editor"
                      setRef={setRequestEditorRef}
                      data={formatString(request)}
                      view={false}
                      mode="code"
                      id={'JsonEditorSpec'}
                      allowedModes={['code', 'tree']}
                      theme="ace/theme/github"
                      htmlElementProps={{
                        style: {
                          height: '60vh',
                          width: '100%'
                        }
                      }}
                    />
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={6}>
            <Accordion elevation={0} expanded={response !== ''}>
              <AccordionSummary
                expandIcon={<ArrowChevronDown color="inherit" />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>Response Received</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {response && (
                  <Grid item xs={12}>
                    <JsonEditor
                      data-id="transform-spec-editor"
                      setRef={setResponseEditorRef}
                      data={formatString(response)}
                      view={false}
                      mode="code"
                      id={'JsonEditorSpec'}
                      allowedModes={['code', 'tree']}
                      theme="ace/theme/github"
                      htmlElementProps={{
                        style: {
                          height: '60vh',
                          width: '100%'
                        }
                      }}
                    />
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
export default CertificationForm;
